import React, { FC, useState } from 'react'
import { Row, Col, Tag, Avatar, Button, Space, Tooltip } from 'antd'
import { EditOutlined } from '@ant-design/icons'

import { OrgClaim } from '../../Routes'
import { UserItemData, UserInviteStatus } from '~/Services/UserService.d'
import styles from './Users.module.less'
import { Utils } from '~/Utils'
import { EditUserModal } from './EditUserModal'

export const tagColor = (text: string) => {
  let color = 'blue'
  switch (text) {
    case OrgClaim.Member:
      color = 'green'
      break
    case OrgClaim.Superuser:
      color = 'blue'
      break
    case UserInviteStatus.Declined:
    case OrgClaim.Admin:
      color = 'red'
      break
    case UserInviteStatus.Accepted:
      color = 'blue'
      break
    case UserInviteStatus.Pending:
      color = 'orange'
      break
    default:
      color = Utils.stringToColour(text)
      break
  }
  return color
}

interface UserItemProps {
  user: UserItemData
  hasAdmin: boolean
  onRefresh?: () => void
}

export const UserItem: FC<UserItemProps> = ({
  user,
  hasAdmin,
  onRefresh,
}) => {
  const fullname = Utils.combineStrings([user.firstname, user.lastname])
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsEditModalVisible(true)
  }

  const handleEditSuccess = async () => {
    if (onRefresh) {
      await onRefresh()
    }
  }

  return (
    <>
      <div key={user.id} className={styles.UserItem}>
        <Row gutter={[8, 8]} align="middle">
          {/* Column 1: Avatar + Fullname */}
          <Col xs={24} sm={6} md={6}>
            <i style={{ fontStyle: 'normal', paddingRight: '8px' }}>
              <Avatar
                src={
                  user.avatarUrl && !user.avatarUrl.startsWith('http')
                    ? `https://${user.avatarUrl}`
                    : undefined
                }
                style={{
                  backgroundColor: user.avatarUrl
                    ? undefined
                    : Utils.stringToColour(user.id),
                }}
              >
                {fullname
                  ? fullname.charAt(0).toLocaleUpperCase()
                  : user.email.charAt(0).toLocaleUpperCase()}
              </Avatar>
            </i>
            {fullname}
          </Col>

          {/* Column 2: Email */}
          <Col xs={24} sm={6} md={6}>
            {user.email}
          </Col>

          {/* Column 3: Organization Roles */}
          <Col xs={24} sm={5} md={5}>
            {user.organizationRoles.map((tag) => (
              <Tag key={tag} color={tagColor(tag)}>
                {tag}
              </Tag>
            ))}
          </Col>

          {/* Column 4: Invitation Status */}
          <Col xs={24} sm={4} md={4}>
            {user.invitationStatus && (
              <Tag color={tagColor(user.invitationStatus)}>
                {user.invitationStatus}
              </Tag>
            )}
          </Col>

          {/* Column 5: Actions (keep layout using empty div if not admin) */}
          <Col xs={24} sm={3} md={3}>
            {hasAdmin ? (
              <Space size="middle">
                <Tooltip title="Edit">
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={handleEditClick}
                    style={{ padding: 0, fontSize: '16px' }}
                  />
                </Tooltip>
              </Space>
            ) : (
              <div style={{ height: 32 }} />
            )}
          </Col>
        </Row>
      </div>

      <EditUserModal
        visible={isEditModalVisible}
        user={user}
        onClose={() => setIsEditModalVisible(false)}
        onSuccess={handleEditSuccess}
      />
    </>
  )
}